// src/pages/dashboard/chapterPage/ChapterAgentPanel.jsx
import React, { Component } from 'react';
import { urlMap, getAuthHeaders } from '../../../utils';
import { AgentStepsChips, AgentStatusPanel, AgentControlButtons, AgentLogs, connectLogStream, disconnectLogStream, parseLogEvent } from '../../components/agent';
import StepResultsModal from './StepResultsModal';
import ScenesModal from '../projectPage/chaptersContainer/ScenesModal';
import { Icon } from '../../components/Icon';

/**
 * ChapterAgentPanel
 *
 * Runs the chapter_stage agent pipeline for a single chapter.
 * Reuses the same sub-components as the project-level AgentPanel.
 *
 * Props:
 *  - projectId: string
 *  - chapterId: string
 *  - onClose: () => void
 *  - onAnalysisComplete: () => void  (optional callback when results are ready)
 */
export default class ChapterAgentPanel extends Component {
  state = {
    agentRunning: false,
    agentPaused: false,
    agentStep: 0,
    agentDone: false,
    agentError: null,
    executionId: null,
    canResume: false,
    stepResults: [],
    atCheckpoint: false,
    nextStep: null,
    completedStep: null,
    selectedStep: null,
    currentlyExecutingStep: null,
    currentStep: null,
    menuData: null,
    menuLoading: true,
    activeStepModal: null, // step name to show results for
    showScenesModal: false,
    agentLogs: [],
    logsExpanded: true,
  };

  agentTimer = null;
  agentDoneTimeout = null;
  logSource = null;

  // Steps that have viewable results
  static VIEWABLE_STEPS = new Set([
    'DetectShowVsTellStep',
    'DetectPacingIssuesStep',
    'ChapterHooksAndScenesStep',
    'DetectChapterHolesStep',
  ]);


  componentDidMount() {
    this.fetchMenuData();
    this.fetchLatestExecution();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.chapterId !== prevProps.chapterId) {
      // Reset when chapter changes
      this.resetState();
    }
    // Connect/disconnect SSE log stream when executionId changes
    if (this.state.executionId !== prevState.executionId) {
      this.disconnectLogs();
      if (this.state.executionId) {
        this.setState({ agentLogs: [] });
        this.connectLogs(this.state.executionId);
      }
    }
  }

  componentWillUnmount() {
    this.clearTimer();
    this.disconnectLogs();
    if (this.agentDoneTimeout) {
      clearTimeout(this.agentDoneTimeout);
      this.agentDoneTimeout = null;
    }
  }

  /* ────────── SSE log stream ────────── */

  connectLogs = (executionId) => {
    this.logSource = connectLogStream(executionId, (event) => {
      this.setState(s => ({ agentLogs: [...s.agentLogs, event] }));
      parseLogEvent(event, {
        onStepStart: this.handleStepStart,
        onStepCompleted: this.handleStepCompleted,
        onCheckpointReached: this.handleCheckpointReached,
        onExecutionComplete: this.handleExecutionComplete,
      });
    });
  };

  disconnectLogs = () => {
    disconnectLogStream(this.logSource);
    this.logSource = null;
  };

  resetState = () => {
    this.clearTimer();
    this.disconnectLogs();
    this.setState({
      agentRunning: false, agentPaused: false, agentStep: 0,
      agentDone: false, agentError: null, executionId: null,
      canResume: false, stepResults: [], atCheckpoint: false,
      nextStep: null, completedStep: null, selectedStep: null,
      currentlyExecutingStep: null, currentStep: null,
      agentLogs: [],
    });
  };

  fetchMenuData = async () => {
    try {
      const response = await fetch(urlMap.getAgentMenu('chapter_stage'), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch menu data');
      const data = await response.json();
      this.setState({ menuData: data, menuLoading: false });
    } catch (error) {
      console.error('[ChapterAgent] Error fetching menu data:', error);
      this.setState({ menuLoading: false });
    }
  };

  getAgentSteps = () => {
    return this.state.menuData?.steps || [];
  };

  fetchLatestExecution = async () => {
    const { projectId } = this.props;
    if (!projectId) return;
    try {
      const response = await fetch(urlMap.getLatestAgentExecution(projectId), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch latest execution');
      const data = await response.json();
      if (!data.execution) return;

      const execution = data.execution;
      // Only reconnect to chapter_stage executions
      if (execution.stage !== 'chapter_stage') return;

      console.log('[ChapterAgent] Latest execution:', execution);

      if (execution.status === 'running') {
        this.setState({
          executionId: execution.id,
          agentRunning: true,
          agentPaused: false,
          stepResults: execution.stepsExecuted || [],
          currentStep: execution.currentStep,
        });
      } else if (execution.status === 'paused') {
        const lastStep = execution.stepsExecuted?.[execution.stepsExecuted.length - 1];
        this.setState({
          executionId: execution.id,
          agentRunning: false,
          agentPaused: true,
          atCheckpoint: true,
          completedStep: lastStep?.step,
          nextStep: execution.nextStep,
          canResume: true,
          stepResults: execution.stepsExecuted || [],
          currentStep: execution.currentStep,
        });
      } else if (execution.status === 'cancelled') {
        this.setState({
          executionId: execution.id,
          agentRunning: false,
          agentPaused: false,
          atCheckpoint: false,
          canResume: false,
          stepResults: execution.stepsExecuted || [],
          agentError: execution.error || 'Execution cancelled',
          currentStep: execution.currentStep,
        });
      } else if (execution.status === 'completed' || execution.status === 'failed') {
        this.setState({
          executionId: execution.id,
          stepResults: execution.stepsExecuted || [],
          agentError: execution.status === 'failed' ? execution.error : null,
          currentStep: execution.currentStep,
        });
      }
    } catch (error) {
      console.error('[ChapterAgent] Error fetching latest execution:', error);
    }
  };

  /* ────────── agent control ────────── */

  startAgent = async (options = {}) => {
    if (this.state.agentRunning) return;
    const { projectId, chapterId } = this.props;
    if (!projectId || !chapterId) return;

    const {
      fromStep = null,
      forceRerun = false,
      preserveStepResults = false,
    } = options;

    this.setState({
      agentRunning: true, agentStep: 0, agentDone: false,
      agentError: null,
      stepResults: preserveStepResults ? this.state.stepResults : [],
      selectedStep: fromStep || null,
    });

    this.agentTimer = setInterval(() => {
      this.setState(s => ({ agentStep: Math.min(s.agentStep, 0) }));
    }, 1000);

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const url = urlMap.executeChapterAgent(projectId, chapterId);

      const response = await fetch(url, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          ...(fromStep ? { fromStep } : {}),
          ...(forceRerun ? { forceRerun: true } : {}),
        }),
      });

      if (!response.ok && response.status !== 202) {
        const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(error.message || error.error || 'Agent execution failed');
      }

      const result = await response.json();
      console.log('[ChapterAgent] Execution started:', result);
      this.setState({ executionId: result.executionId });

      if (response.status === 202) return; // SSE will drive updates

      if (result.success) {
        this.clearTimer();
        this.setState({ agentDone: true, agentError: null, stepResults: result.results || [] });
        this.props.onAnalysisComplete?.();
        this.autoReset();
      } else {
        throw new Error(result.error || 'Agent execution failed');
      }
    } catch (error) {
      console.error('[ChapterAgent] Execution error:', error);
      this.clearTimer();
      this.setState({
        agentRunning: false, agentStep: 0, agentDone: false,
        agentError: error.message || 'Failed to run agent',
        canResume: !!this.state.executionId,
      });
    }
  };

  resumeAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;

    this.setState({
      agentRunning: true, agentPaused: false,
      agentError: null, canResume: false, atCheckpoint: false,
    });
    this.agentTimer = setInterval(() => this.setState(s => ({ agentStep: Math.min(s.agentStep, 0) })), 1000);

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await fetch(urlMap.resumeAgent(executionId), {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ stage: 'chapter_stage' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Resume failed');
      }
      const result = await response.json();

      if (result.success) {
        this.clearTimer();
        this.setState({ agentDone: true, agentError: null, stepResults: result.results || [] });
        this.props.onAnalysisComplete?.();
        this.autoReset();
      } else {
        throw new Error(result.error || 'Resume failed');
      }
    } catch (error) {
      console.error('[ChapterAgent] Resume error:', error);
      this.clearTimer();
      this.setState({ agentRunning: false, agentError: error.message, canResume: true });
    }
  };

  continueAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;

    this.setState({
      agentRunning: true, agentPaused: false,
      agentError: null, canResume: false, atCheckpoint: false,
    });
    this.agentTimer = setInterval(() => this.setState(s => ({ agentStep: Math.min(s.agentStep, 0) })), 1000);

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await fetch(urlMap.continueAgent(executionId), {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ stage: 'chapter_stage' }),
      });
      if (!response.ok && response.status !== 202) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Continue failed');
      }
      // 202 = running in background — SSE stream will deliver checkpoint/completion events
      if (response.status === 202) return;

      // Legacy sync 200 path (kept for safety)
      const result = await response.json();
      const prev = this.state.stepResults || [];
      const merged = [...prev];
      (result.results || []).forEach(nr => {
        if (!merged.find(pr => pr.step === nr.step)) merged.push(nr);
      });
      if (result.paused) {
        this.clearTimer();
        const last = result.results[result.results.length - 1];
        this.setState({
          agentRunning: false, agentPaused: true, atCheckpoint: true,
          nextStep: result.nextStep, completedStep: last?.step,
          canResume: true, stepResults: merged, agentError: null,
        });
        return;
      }
      if (result.success) {
        this.clearTimer();
        this.setState({ agentDone: true, agentError: null, stepResults: merged, atCheckpoint: false });
        this.props.onAnalysisComplete?.();
        this.autoReset();
      } else {
        throw new Error(result.error || 'Continue failed');
      }
    } catch (error) {
      console.error('[ChapterAgent] Continue error:', error);
      this.clearTimer();
      this.setState({ agentRunning: false, agentError: error.message, canResume: true, atCheckpoint: true });
    }
  };

  pauseAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;
    try {
      const response = await fetch(urlMap.pauseAgent(executionId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Pause failed');
      }
      this.clearTimer();
      this.setState({ agentRunning: false, agentPaused: true, canResume: true, agentError: null });
    } catch (error) {
      console.error('[ChapterAgent] Pause error:', error);
      this.setState({ agentError: error.message });
    }
  };

  cancelAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;

    try {
      const response = await fetch(urlMap.cancelAgent(executionId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Cancel failed');
      }

      this.clearTimer();
      this.setState({
        agentRunning: false,
        agentPaused: false,
        atCheckpoint: false,
        canResume: false,
        currentlyExecutingStep: null,
        agentError: 'Execution cancelled by user',
      });
    } catch (error) {
      console.error('[ChapterAgent] Cancel error:', error);
      this.setState({ agentError: error.message || 'Cancel failed' });
    }
  };

  stopAgent = () => {
    this.clearTimer();
    if (this.agentDoneTimeout) { clearTimeout(this.agentDoneTimeout); this.agentDoneTimeout = null; }
    this.setState({
      agentRunning: false, agentPaused: false, agentStep: 0,
      agentDone: false, agentError: null, canResume: false,
      atCheckpoint: false, nextStep: null, completedStep: null,
    });
  };

  resetAndRun = () => {
    this.clearTimer();
    if (this.agentDoneTimeout) {
      clearTimeout(this.agentDoneTimeout);
      this.agentDoneTimeout = null;
    }

    this.setState({
      agentRunning: false,
      agentPaused: false,
      agentStep: 0,
      agentDone: false,
      agentError: null,
      canResume: false,
      atCheckpoint: false,
      nextStep: null,
      completedStep: null,
      selectedStep: null,
      stepResults: [],
      executionId: null,
      currentStep: null,
      currentlyExecutingStep: null,
      agentLogs: [],
    }, () => {
      this.startAgent();
    });
  };

  handleStepSelect = (stepName) => {
    if (this.state.agentRunning || this.state.agentPaused) return;
    this.setState(prev => ({
      selectedStep: prev.selectedStep === stepName ? null : stepName,
    }));
  };

  handleRerunFromStep = (stepName) => {
    if (this.state.agentRunning || this.state.agentPaused) return;

    const agentSteps = this.getAgentSteps();
    const fromIdx = agentSteps.findIndex(s => s.name === stepName);
    const clearedResults = fromIdx > 0
      ? this.state.stepResults.filter(r => {
          const rIdx = agentSteps.findIndex(s => s.name === r.step);
          return rIdx >= 0 && rIdx < fromIdx;
        })
      : [];

    this.setState({
      selectedStep: stepName,
      stepResults: clearedResults,
      agentDone: false,
      agentError: null,
      canResume: false,
      atCheckpoint: false,
    }, () => {
      this.startAgent({
        fromStep: stepName,
        forceRerun: true,
        preserveStepResults: true,
      });
    });
  };

  /* ────────── step results modal ────────── */

  openStepResults = (stepName) => {
    // All steps use the StepResultsModal now (including hooks+scenes combined)
    this.setState({ activeStepModal: stepName });
  };
  closeStepResults = () => this.setState({ activeStepModal: null });
  closeScenesModal = () => this.setState({ showScenesModal: false });

  getCompletedStepNames = () => {
    const { stepResults } = this.state;
    const set = new Set();
    if (stepResults && stepResults.length > 0) {
      stepResults.forEach(r => { if (r.success) set.add(r.step); });
    }
    return set;
  };

  getNextUnexecutedStep = () => {
    const agentSteps = this.getAgentSteps();
    const completedStepNames = this.getCompletedStepNames();
    return agentSteps.find(step => !completedStepNames.has(step.name));
  };

  /* ────────── SSE handlers ────────── */

  handleStepStart = (stepInfo) => {
    this.setState({ currentlyExecutingStep: stepInfo.step });
  };

  handleStepCompleted = (stepInfo) => {
    console.log('[ChapterAgent] Step completed via SSE:', stepInfo.step, stepInfo.success);
    this.setState(prev => {
      const existing = prev.stepResults.find(r => r.step === stepInfo.step);
      if (existing) return null; // already tracked
      return {
        stepResults: [...prev.stepResults, { step: stepInfo.step, success: stepInfo.success }],
        currentlyExecutingStep: null,
      };
    });
    // Notify parent immediately when a step finishes
    if (stepInfo.success) {
      this.props.onAnalysisComplete?.();
    }
  };

  handleCheckpointReached = async (checkpointInfo) => {
    console.log('[ChapterAgent] Checkpoint reached:', checkpointInfo);
    this.clearTimer();

    try {
      const response = await fetch(urlMap.getAgentExecution(checkpointInfo.executionId), {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (response.ok) {
        const execution = await response.json();
        const completedStepName = checkpointInfo.step || execution.currentStep;
        const agentSteps = this.getAgentSteps();
        const currentIndex = agentSteps.findIndex(s => s.name === completedStepName);
        const nextStepConfig = currentIndex >= 0 && currentIndex < agentSteps.length - 1
          ? agentSteps[currentIndex + 1] : null;

        this.setState({
          agentRunning: false, agentPaused: true, atCheckpoint: true,
          nextStep: nextStepConfig?.name, completedStep: completedStepName, canResume: true,
        });
      }
    } catch (error) {
      console.error('[ChapterAgent] Failed to fetch execution state:', error);
      this.setState({
        agentRunning: false, agentPaused: true, atCheckpoint: true,
        completedStep: checkpointInfo.step, canResume: true, currentlyExecutingStep: null,
      });
    }
  };

  handleExecutionComplete = async (result) => {
    console.log('[ChapterAgent] Execution completed:', result);
    this.clearTimer();
    this.setState({ currentlyExecutingStep: null });

    if (result.success) {
      // Fetch full execution state to get final step results
      try {
        const response = await fetch(urlMap.getAgentExecution(result.executionId), {
          headers: getAuthHeaders(), credentials: 'include',
        });
        if (response.ok) {
          const execution = await response.json();
          this.setState({
            agentDone: true,
            agentRunning: false,
            agentError: null,
            stepResults: execution.stepsExecuted || this.state.stepResults,
          });
        } else {
          this.setState({ agentDone: true, agentRunning: false, agentError: null });
        }
      } catch (error) {
        console.error('[ChapterAgent] Failed to fetch execution results:', error);
        this.setState({ agentDone: true, agentRunning: false, agentError: null });
      }
      this.props.onAnalysisComplete?.();
    } else {
      this.setState({ agentRunning: false, agentError: result.error || 'Execution failed', agentDone: false });
    }
  };

  /* ────────── utilities ────────── */

  clearTimer = () => {
    if (this.agentTimer) { clearInterval(this.agentTimer); this.agentTimer = null; }
  };

  autoReset = () => {
    this.agentDoneTimeout = setTimeout(() => {
      this.setState({
        agentRunning: false, agentStep: 0, agentDone: false,
        stepResults: [], executionId: null,
      });
    }, 3000);
  };

  /* ────────── render ────────── */

  render() {
    const { onClose, projectId, chapterId } = this.props;
    const {
      agentRunning, agentPaused, agentStep, agentDone, agentError,
      canResume, atCheckpoint, completedStep, executionId,
      activeStepModal, showScenesModal, menuLoading,
    } = this.state;

    const agentSteps = this.getAgentSteps();
    const stepsReady = !menuLoading && agentSteps.length > 0;
    const completedStepNames = this.getCompletedStepNames();

    const getStepTitle = (stepName) => {
      const step = agentSteps.find(s => s.name === stepName);
      return step?.title || stepName;
    };

    // Get minimal chapter info for modal
    const chapter = this.props.chapter || { id: chapterId };
    const metadata = chapter.metadata || {};
    const order_no = chapter.chapter_number;
    const title = chapter.title;

    return (
      <div className="chapter-agent-scroll">
        <aside className="chapter-agent-panel" aria-label="Chapter analysis agent">
          <div className="chapter-agent-header">
            <span className="chapter-agent-title">Storyclub Agent</span>
            <button className="btn subtle" onClick={onClose} title="Close agent">
              <Icon.Close />
            </button>
          </div>

          <div className="chapter-agent-body">
            {/* Loading steps from backend */}
            {menuLoading && (
              <div className="agent-panel" aria-live="polite">
                <div className="agent-row">
                  <div className="agent-spinner" aria-hidden="true" />
                  <div className="agent-text">Loading steps…</div>
                </div>
              </div>
            )}

            {!menuLoading && agentSteps.length === 0 && (
              <div className="agent-error">No steps configured for chapter analysis.</div>
            )}

            {/* Spinner while running */}
            {stepsReady && agentRunning && (
              <AgentStatusPanel
                agentDone={agentDone}
                agentSteps={agentSteps}
                agentStep={agentStep}
              />
            )}

            {/* Checkpoint dialog */}
            {stepsReady && atCheckpoint && (
              <div className="checkpoint-dialog">
                <div className="checkpoint-content">
                  <span className="checkpoint-icon">✓</span>
                  <span className="checkpoint-text">
                    <strong>{getStepTitle(completedStep)}</strong> complete
                  </span>
                </div>
                <div className="checkpoint-actions">
                  <button type="button" className="checkpoint-btn checkpoint-btn--continue" onClick={this.continueAgent}>Continue</button>
                  <button type="button" className="checkpoint-btn checkpoint-btn--stop" onClick={this.stopAgent}>Stop</button>
                </div>
              </div>
            )}

            {/* Control buttons */}
            {stepsReady && !atCheckpoint && (
              <AgentControlButtons
                agentRunning={agentRunning}
                agentPaused={agentPaused}
                agentDone={agentDone}
                agentError={agentError}
                canResume={canResume}
                canCancel={!!executionId}
                startLabel="Run"
                doneLabel={this.getNextUnexecutedStep() ? 'Continue' : 'Restart'}
                onStart={this.startAgent}
                onPause={this.pauseAgent}
                onCancel={this.cancelAgent}
                onResume={this.resumeAgent}
                onStop={this.stopAgent}
                onReset={this.resetAndRun}
              />
            )}

            {/* Steps chips */}
            {stepsReady && (
              <AgentStepsChips
                steps={agentSteps}
                currentStep={this.state.currentStep}
                currentlyExecutingStep={this.state.currentlyExecutingStep}
                isRunning={agentRunning}
                isPaused={agentPaused}
                selectedStep={this.state.selectedStep}
                onStepSelect={this.handleStepSelect}
                onRerunFromStep={this.handleRerunFromStep}
                stepResults={this.state.stepResults}
                onViewResults={this.openStepResults}
                viewableSteps={ChapterAgentPanel.VIEWABLE_STEPS}
                metadata={chapter.metadata}
              />
            )}

            {/* Logs */}
            <AgentLogs
              logs={this.state.agentLogs}
              visible={!!executionId}
              isExpanded={this.state.logsExpanded}
              onToggleExpanded={() => this.setState(s => ({ logsExpanded: !s.logsExpanded }))}
              onClearLogs={() => this.setState({ agentLogs: [] })}
            />
          </div>
        </aside>

        {/* Step results modal */}
        <StepResultsModal
          isOpen={!!activeStepModal}
          onClose={this.closeStepResults}
          metadata={metadata}
          chapter={chapter}
          order_no={order_no}
          title={title}
          projectId={projectId}
          stepName={activeStepModal}
          stepLabel={getStepTitle(activeStepModal)}
        />
      </div>
    );
  }
}
